// Full-page paywall for directly-navigable create routes (COL-352):
// /admin/properties/new and /admin/tenants/new can be reached by URL, so the
// button-level guardCreate alone would be bypassable UI. Staff/owners and
// entitled operators pass straight through.
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useOperatorEntitlement } from "@/hooks/useOperatorEntitlement";
import {
  paywallCopy,
  PAYWALL_HIGHLIGHTS,
  type PaywallTrigger,
} from "@/components/paywall/PaywallDialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Lock, Check } from "lucide-react";
import { AdminV2Layout } from "@/components/admin/v2/AdminV2Layout";

export function EntitlementRouteGate({
  trigger,
  children,
}: {
  trigger: PaywallTrigger;
  children: ReactNode;
}) {
  const navigate = useNavigate();
  const { isOperatorPortal, rolesReady } = useAuth();
  const { isEntitled, isLoading } = useOperatorEntitlement();

  if (!rolesReady || !isOperatorPortal || isEntitled) return <>{children}</>;
  if (isLoading) return null; // verdict pending — render nothing, not a flash

  const { title, body } = paywallCopy(trigger);
  // Wrap in AdminV2Layout so the paywall keeps the admin top bar (H1 = title).
  // The card sells the subscription — lock badge, the reason, and a checklist
  // of what one subscription turns on — rather than a bare stranded box.
  return (
    <AdminV2Layout title={title}>
      <div className="mx-auto max-w-xl">
        <Card className="border-primary/15 shadow-sm">
          <CardContent className="space-y-6 p-6 sm:p-8">
            <div className="flex items-start gap-4">
              <span
                className="flex h-12 w-12 flex-none items-center justify-center rounded-xl bg-primary/10 text-primary"
                aria-hidden
              >
                <Lock className="h-5 w-5" />
              </span>
              <div className="space-y-1.5">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Operator subscription
                </p>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  {body}
                </p>
              </div>
            </div>

            <div className="rounded-lg border bg-muted/30 p-4">
              <p className="mb-3 text-sm font-medium text-foreground">
                One subscription unlocks
              </p>
              <ul className="space-y-2.5">
                {PAYWALL_HIGHLIGHTS.map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm">
                    <Check
                      className="mt-0.5 h-4 w-4 flex-none text-primary"
                      aria-hidden
                    />
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <Button size="lg" onClick={() => navigate("/admin/billing")}>
                Subscribe
              </Button>
              <Button
                variant="ghost"
                size="lg"
                onClick={() => navigate("/admin?tab=analytics")}
              >
                Back to overview
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminV2Layout>
  );
}
